import React, { useState, useEffect } from 'react';
import { API_BASE as API } from '../../lib/apiBase';
import {
  Radio, Eye, Users, Trash2, AlertTriangle, Zap,
  StopCircle, Crown, MessageSquare, Ban, RefreshCw,
  Settings2, DollarSign, Shield, Activity, Mic, MicOff
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   LIVE GOD MODE PANEL — Maximum Supervision Control
   
   Available for: Admin (pulsestockia@gmail.com) & Pluka (alan.inca6@gmail.com)
   
   Features:
   - View ALL live sessions in real-time
   - Force end any session
   - Ban users from live
   - Mute hosts
   - View all chat messages
   - Monitor donations and tips
   - Adjust commission (ADMIN ONLY)
   
   Commission split: 70% Creator, 15% Pluka, 15% Admin (FIXED)
   Only Admin can modify the commission percentage.
═══════════════════════════════════════════════════════════════════════════════ */


export const LiveGodModePanel = ({ token, isOwner, lang }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [commissionSettings, setCommissionSettings] = useState({
    total_rate: 0.30,
    creator_share: 0.70,
    pluka_share: 0.15,
    admin_share: 0.15
  });
  const [editingCommission, setEditingCommission] = useState(false);
  const [stats, setStats] = useState({
    total_sessions: 0,
    active_sessions: 0,
    total_viewers: 0,
    total_donations: 0,
    total_platform_earnings: 0
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchSessions = async () => {
    try {
      const [sessionsRes, statsRes, commissionRes] = await Promise.all([
        fetch(`${API}/api/admin/live/all`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/live/stats`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/commission`, { headers }).then(r => r.json())
      ]);
      setSessions(sessionsRes.sessions || []);
      setStats(statsRes || {});
      setCommissionSettings(commissionRes || commissionSettings);
    } catch (e) {
      console.error('Failed to fetch god mode data:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const forceEndSession = async (sessionId) => {
    if (!window.confirm(lang === 'es' ? '¿Forzar fin de esta sesión?' : 'Force end this session?')) return;
    setActionLoading(sessionId);
    try {
      await fetch(`${API}/api/admin/live/${sessionId}/force-end`, { method: 'POST', headers });
      fetchSessions();
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setActionLoading(null);
  };

  const banUserFromLive = async (userId, sessionId) => {
    if (!window.confirm(lang === 'es' ? '¿Banear este usuario de Live?' : 'Ban this user from Live?')) return;
    setActionLoading(userId);
    try {
      await fetch(`${API}/api/admin/live/ban`, { 
        method: 'POST', 
        headers,
        body: JSON.stringify({ user_id: userId, session_id: sessionId, reason: 'Admin action' })
      });
      fetchSessions();
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setActionLoading(null);
  };

  const clearChat = async (sessionId) => {
    if (!window.confirm(lang === 'es' ? '¿Limpiar todo el chat?' : 'Clear all chat messages?')) return;
    setActionLoading(`chat-${sessionId}`);
    try {
      await fetch(`${API}/api/admin/live/${sessionId}/clear-chat`, { method: 'POST', headers });
      fetchSessions();
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setActionLoading(null);
  };

  const saveCommission = async () => {
    if (!isOwner) {
      alert(lang === 'es' ? 'Solo Admin puede modificar la comisión' : 'Only Admin can modify commission');
      return;
    }
    try {
      await fetch(`${API}/api/admin/commission`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(commissionSettings)
      });
      setEditingCommission(false);
      fetchSessions();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const formatEuro = (n) => (n || 0).toFixed(2) + '€';
  const formatTime = (iso) => iso ? new Date(iso).toLocaleString() : '-';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="live-god-mode-panel" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
            <Shield size={20} className="text-red-500" />
          </div>
          {lang === 'es' ? 'Control Total Live (Modo Dios)' : 'Live God Mode Control'}
        </h2>
        <button 
          onClick={fetchSessions}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-pulse-elevated border border-pulse-border text-sm hover:bg-pulse-surface transition"
        >
          <RefreshCw size={14} />
          {lang === 'es' ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
          <p className="text-xs text-pulse-muted mb-1">{lang === 'es' ? 'Total Sesiones' : 'Total Sessions'}</p>
          <p className="text-2xl font-bold">{stats.total_sessions || 0}</p>
        </div>
        <div className="bg-pulse-surface border border-red-500/30 rounded-xl p-4">
          <p className="text-xs text-pulse-muted mb-1 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            {lang === 'es' ? 'En Vivo Ahora' : 'Live Now'}
          </p>
          <p className="text-2xl font-bold text-red-400">{stats.active_sessions || 0}</p>
        </div>
        <div className="bg-pulse-surface border border-purple-500/30 rounded-xl p-4">
          <p className="text-xs text-pulse-muted mb-1">{lang === 'es' ? 'Viewers Totales' : 'Total Viewers'}</p>
          <p className="text-2xl font-bold text-purple-400">{stats.total_viewers || 0}</p>
        </div>
        <div className="bg-pulse-surface border border-green-500/30 rounded-xl p-4">
          <p className="text-xs text-pulse-muted mb-1">{lang === 'es' ? 'Apoyos Totales' : 'Total Supports'}</p>
          <p className="text-2xl font-bold text-green-400">{formatEuro(stats.total_donations)}</p>
        </div>
        <div className="bg-pulse-surface border border-yellow-500/30 rounded-xl p-4">
          <p className="text-xs text-pulse-muted mb-1">{lang === 'es' ? 'Ganancias Plataforma' : 'Platform Earnings'}</p>
          <p className="text-2xl font-bold text-yellow-400">{formatEuro(stats.total_platform_earnings)}</p>
        </div>
      </div>

      {/* Commission Control - ADMIN ONLY */}
      <div className="bg-pulse-surface border border-yellow-500/20 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <DollarSign size={18} className="text-yellow-400" />
            {lang === 'es' ? 'División de Comisiones (Fijo)' : 'Commission Split (Fixed)'}
          </h3>
          {isOwner && !editingCommission && (
            <button
              data-testid="edit-commission-btn"
              onClick={() => setEditingCommission(true)}
              className="text-xs px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/20 transition"
            >
              {lang === 'es' ? 'Modificar' : 'Edit'}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-pulse-elevated rounded-xl p-4 text-center">
            <p className="text-xs text-pulse-muted mb-1">{lang === 'es' ? 'Comisión Total' : 'Total Fee'}</p>
            {editingCommission && isOwner ? (
              <input
                data-testid="commission-total-input"
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={commissionSettings.total_rate}
                onChange={e => setCommissionSettings(c => ({ ...c, total_rate: parseFloat(e.target.value) }))}
                className="w-20 text-center bg-pulse-surface border border-yellow-500/30 rounded-lg px-2 py-1 text-lg font-bold text-yellow-400"
              />
            ) : (
              <p className="text-2xl font-bold text-yellow-400">{(commissionSettings.total_rate * 100).toFixed(0)}%</p>
            )}
          </div>
          <div className="bg-pulse-elevated rounded-xl p-4 text-center">
            <p className="text-xs text-pulse-muted mb-1">{lang === 'es' ? 'Creador' : 'Creator'}</p>
            <p className="text-2xl font-bold text-green-400">{((1 - commissionSettings.total_rate) * 100).toFixed(0)}%</p>
          </div>
          <div className="bg-pulse-elevated rounded-xl p-4 text-center">
            <p className="text-xs text-pulse-muted mb-1">Pluka (alan.inca6)</p>
            <p className="text-2xl font-bold text-cyan-400">{(commissionSettings.pluka_share * 100).toFixed(0)}%</p>
          </div>
          <div className="bg-pulse-elevated rounded-xl p-4 text-center">
            <p className="text-xs text-pulse-muted mb-1">Admin (pulsestockia)</p>
            <p className="text-2xl font-bold text-purple-400">{(commissionSettings.admin_share * 100).toFixed(0)}%</p>
          </div>
        </div>

        {!isOwner && (
          <p data-testid="commission-readonly-notice" className="text-xs text-yellow-300 flex items-center gap-2">
            <AlertTriangle size={12} />
            {lang === 'es' 
              ? 'Solo Admin puede modificar el porcentaje de comisión. Pluka gestiona todo excepto esto.'
              : 'Only Admin can modify commission percentage. Pluka manages everything except this.'}
          </p>
        )}

        {editingCommission && isOwner && (
          <div className="flex items-center gap-3 mt-4">
            <button
              data-testid="save-commission-btn"
              onClick={saveCommission}
              className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-semibold text-sm hover:bg-yellow-400 transition"
            >
              {lang === 'es' ? 'Guardar Comisión' : 'Save Commission'}
            </button>
            <button
              onClick={() => setEditingCommission(false)}
              className="px-4 py-2 rounded-xl bg-pulse-elevated text-pulse-muted text-sm hover:text-white transition"
            >
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="bg-pulse-surface border border-red-500/20 rounded-xl p-6">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
          <Radio size={18} className="text-red-500" />
          {lang === 'es' ? 'Sesiones Activas (Control Total)' : 'Active Sessions (Full Control)'}
        </h3>

        {sessions.filter(s => s.status === 'live').length === 0 ? (
          <p className="text-pulse-muted text-center py-8">
            {lang === 'es' ? 'No hay sesiones en vivo ahora' : 'No live sessions right now'}
          </p>
        ) : (
          <div className="space-y-3">
            {sessions.filter(s => s.status === 'live').map(session => (
              <div 
                key={session.id}
                data-testid={`god-session-${session.id}`}
                className="bg-pulse-elevated rounded-xl p-4 border border-red-500/30"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                    <div>
                      <p className="font-semibold">{session.title || 'Sin título'}</p>
                      <p className="text-xs text-pulse-muted">
                        Host: {session.host_name || session.artist_name} ({session.host_email || 'N/A'})
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 flex items-center gap-1">
                      <Eye size={10} /> {session.viewers || 0}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                      {formatEuro(session.total_donations)}
                    </span>
                  </div>
                </div>

                {/* Session Details */}
                <div className="grid grid-cols-4 gap-4 text-xs mb-4">
                  <div>
                    <p className="text-pulse-muted">{lang === 'es' ? 'Inicio' : 'Started'}</p>
                    <p>{formatTime(session.started_at)}</p>
                  </div>
                  <div>
                    <p className="text-pulse-muted">{lang === 'es' ? 'Formato' : 'Format'}</p>
                    <p className="capitalize">{session.session_format || 'room'}</p>
                  </div>
                  <div>
                    <p className="text-pulse-muted">{lang === 'es' ? 'Mensajes' : 'Messages'}</p>
                    <p>{(session.chat || []).length}</p>
                  </div>
                  <div>
                    <p className="text-pulse-muted">{lang === 'es' ? 'Tier' : 'Tier'}</p>
                    <p className="capitalize">{session.host_fame_tier || 'bronze'}</p>
                  </div>
                </div>

                {/* Control Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-pulse-border">
                  <button
                    data-testid={`force-end-${session.id}`}
                    onClick={() => forceEndSession(session.id)}
                    disabled={actionLoading === session.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-xs font-medium hover:bg-red-500/30 transition disabled:opacity-50"
                  >
                    <StopCircle size={12} />
                    {lang === 'es' ? 'Forzar Fin' : 'Force End'}
                  </button>
                  <button
                    data-testid={`clear-chat-${session.id}`}
                    onClick={() => clearChat(session.id)}
                    disabled={actionLoading === `chat-${session.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-400 text-xs font-medium hover:bg-yellow-500/30 transition disabled:opacity-50"
                  >
                    <Trash2 size={12} />
                    {lang === 'es' ? 'Limpiar Chat' : 'Clear Chat'}
                  </button>
                  <button
                    onClick={() => setSelectedSession(selectedSession?.id === session.id ? null : session)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 text-xs font-medium hover:bg-cyan-500/30 transition"
                  >
                    <MessageSquare size={12} />
                    {lang === 'es' ? 'Ver Chat' : 'View Chat'}
                  </button>
                </div>

                {/* Expanded Chat View */}
                {selectedSession?.id === session.id && (
                  <div className="mt-4 bg-pulse-surface rounded-xl p-4 max-h-64 overflow-y-auto">
                    <p className="text-xs text-pulse-muted mb-2">
                      {lang === 'es' ? 'Chat en tiempo real:' : 'Live chat:'}
                    </p>
                    {(session.chat || []).length === 0 ? (
                      <p className="text-pulse-muted text-xs">{lang === 'es' ? 'Sin mensajes' : 'No messages'}</p>
                    ) : (
                      <div className="space-y-1">
                        {(session.chat || []).slice(-50).map((msg, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <span className="text-pulse-muted">{msg.user_name}:</span>
                            <span className={msg.type === 'tip' ? 'text-yellow-400' : 'text-white/80'}>
                              {msg.message}
                              {msg.type === 'tip' && ` (${formatEuro(msg.amount)})`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Session History */}
      <div className="bg-pulse-surface border border-pulse-border rounded-xl p-6">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
          <Activity size={18} className="text-pulse-muted" />
          {lang === 'es' ? 'Historial de Sesiones' : 'Session History'}
        </h3>

        {sessions.filter(s => s.status !== 'live').length === 0 ? (
          <p className="text-pulse-muted text-center py-8">
            {lang === 'es' ? 'No hay historial' : 'No history'}
          </p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {sessions.filter(s => s.status !== 'live').slice(0, 50).map(session => (
              <div 
                key={session.id}
                className="flex items-center gap-4 p-3 bg-pulse-elevated rounded-xl"
              >
                <div className={`w-2 h-2 rounded-full ${session.status === 'ended' ? 'bg-gray-500' : 'bg-yellow-500'}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{session.host_name || session.artist_name}</p>
                  <p className="text-xs text-pulse-muted">{session.title} • {formatTime(session.started_at)}</p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1 text-purple-400">
                    <Eye size={10} /> {session.peak_viewers || session.viewers || 0}
                  </span>
                  <span className="flex items-center gap-1 text-green-400">
                    {formatEuro(session.total_donations)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full ${
                    session.status === 'ended' ? 'bg-gray-500/20 text-gray-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {session.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
